import React, { useEffect, useRef } from 'react';
import { select } from 'd3-selection';
import { arc } from 'd3-shape';
import Information from 'components/Information';
import TravelGraphsHeading from '../TravelGraphsHeading';
import Styles from './WindGraph.module.scss';

interface WindGraphProps {
  data: {
    month?: string;
    percentage: number;
  }[];
}

const WindGraph: React.FC<WindGraphProps> = ({ data }) => {
  const svgRefs = useRef<(SVGSVGElement | null)[]>([]);

  useEffect(() => {
    data.forEach((item, index) => {
      const svgElement = svgRefs.current[index];
      if (!svgElement) return;

      // Clear previous content
      select(svgElement).selectAll('*').remove();

      const svg = select(svgElement);
      const width = 100;
      const height = 100;
      const radius = 45;
      const strokeWidth = 4;

      // Create the SVG container
      const g = svg
        .attr('viewBox', `0 0 ${width} ${height}`)
        .append('g')
        .attr('transform', `translate(${width / 2}, ${height / 2})`);

      // Create the trail (background circle)
      g.append('path')
        .attr('class', 'CircularProgressbar-trail')
        .attr(
          'd',
          arc()({
            innerRadius: radius - strokeWidth / 2,
            outerRadius: radius + strokeWidth / 2,
            startAngle: 0,
            endAngle: 2 * Math.PI,
          }),
        )
        .attr('fill', 'none')
        .attr('stroke', '#314EE6')
        .attr('stroke-width', strokeWidth)
        .attr('opacity', 0.2);

      // Create the progress path
      const progressAngle = (item.percentage / 100) * 2 * Math.PI;
      // Test if 0 radians corresponds to 12 o'clock in this coordinate system
      const startAngle = 0; // Testing if 0 is actually 12 o'clock
      const endAngle = startAngle + progressAngle; // Go clockwise
      g.append('path')
        .attr('class', 'CircularProgressbar-path')
        .attr(
          'd',
          arc()({
            innerRadius: radius - strokeWidth / 2,
            outerRadius: radius + strokeWidth / 2,
            startAngle,
            endAngle,
          }),
        )
        .attr('fill', 'none')
        .attr('stroke', '#314EE6')
        .attr('stroke-width', strokeWidth)
        .attr('stroke-linecap', 'round');

      // Add the text
      g.append('text')
        .attr('class', 'CircularProgressbar-text')
        .attr('x', 0)
        .attr('y', 0)
        .attr('text-anchor', 'middle')
        .attr('dominant-baseline', 'middle')
        .attr('fill', '#171717')
        .style('font-family', 'var(--product-font-linear)')
        .style('font-weight', '600')
        .style('font-size', '28px')
        .text(`${Math.round(item.percentage)}%`);
    });
  }, [data]);

  return (
    <div className={Styles.windGraph}>
      <div className={Styles.heading}>
        <TravelGraphsHeading heading="Favorable Winds" />
        <Information tooltip="The percentage of daylight hours where the wind is either light or offshore" />
      </div>
      <div className={Styles.windGraphs}>
        {data.map(({ month }, index) => (
          <svg
            key={month}
            ref={(el) => {
              svgRefs.current[index] = el;
            }}
            className="CircularProgressbar"
            style={{ cursor: 'pointer', padding: '0 5px' }}
            role="img"
            aria-label={`${month} wind graph`}
          />
        ))}
      </div>
    </div>
  );
};

export default WindGraph;
